import { useMemo, useRef, useState } from 'react';
import type { TableProps } from 'antd';
import {
  CloudUploadOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileTextOutlined, FileWordOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { AudioLines } from 'lucide-react';

import { DataTable, MenuThaoTac, PageHeader, ThongBaoHetHanMuc } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { tachLoiHanMuc } from '../../../lib/loi-han-muc';
import { ngayGioVN, soVN, KHONG_CO } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { HopDoiTenPhien } from '../components/HopDoiTenPhien';
import { HopTaiBanGhi } from '../components/HopTaiBanGhi';
import { BADGE_TOM_TAT, NHAN_TRANG_THAI, QUYEN } from '../constants';
import { useDanhSachPhien, useGhiPhien } from '../hooks/useBienBan';
import { useXuatPhien } from '../hooks/useXuatPhien';
import { dinhDangThoiLuong } from '../nghiep-vu/bien-ban';
import { TRANG_THAI_PHIEN } from '../types';
import type { Phien, TrangThaiPhien } from '../types';

type LocTrangThai = TrangThaiPhien | 'tat-ca';
const CHO_GO = 350;

/** Danh sách phiên (`/bien-ban`, phân hệ `MEETING_MINUTES`): lọc ở server, `Tải thêm` theo con trỏ, tự hỏi lại khi còn phiên đang chạy. */
export function DanhSachPhienView() {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const [tuKhoa, setTuKhoa] = useState('');
  const [tim, setTim] = useState('');
  const hen = useRef<number | undefined>(undefined);
  const [trangThai, setTrangThai] = useState<LocTrangThai>('tat-ca');
  const [lanMoTai, setLanMoTai] = useState(0);
  const [moTai, setMoTai] = useState(false);
  const [doiTen, setDoiTen] = useState<Phien | null>(null);
  const [xoa, setXoa] = useState<Phien | null>(null);
  const ghi = useGhiPhien();
  const { xuat } = useXuatPhien();

  const q = useDanhSachPhien({ search: tim || undefined, status: trangThai === 'tat-ca' ? undefined : trangThai });
  const dong = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const coLoc = tim !== '' || trangThai !== 'tat-ca';
  const tieuDe = t('bienBan.tieuDe');

  const goTim = (v: string) => {
    setTuKhoa(v);
    window.clearTimeout(hen.current);
    hen.current = window.setTimeout(() => setTim(v.trim()), v ? CHO_GO : 0);
  };
  const moHopTai = () => {
    setLanMoTai((n) => n + 1);
    setMoTai(true);
  };
  const xacNhanXoa = () => {
    if (!xoa) return;
    const ten = xoa.title;
    ghi.xoa.mutate(xoa.id, {
      onSuccess: () => {
        toast.success(t('bienBan.daXoa', { ten }));
        setXoa(null);
      },
    });
  };

  const columns: TableProps<Phien>['columns'] = [
    {
      title: t('bienBan.cot.tieuDe'),
      render: (_v, p) => (
        <button type="button" onClick={() => dieuHuong(`/bien-ban/${p.id}`)} className="flex w-52 min-w-0 flex-col xl:w-80 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500">
          <span className="truncate text-sm font-medium text-primary-700 hover:underline" title={p.title}>{p.title}</span>
          <span className="truncate text-2xs text-content-muted" title={p.file_name}>{p.file_name}</span>
        </button>
      ),
    },
    {
      title: t('bienBan.cot.thoiLuong'),
      width: 110,
      render: (_v, p) => <span className="whitespace-nowrap font-mono text-xs tabular-nums text-content-secondary">{dinhDangThoiLuong(p.duration_seconds) ?? KHONG_CO}</span>,
    },
    {
      title: t('bienBan.cot.soNguoiNoi'),
      width: 110,
      align: 'right',
      render: (_v, p) => <span className="text-xs tabular-nums text-content-secondary">{p.speakers.length > 0 ? soVN(p.speakers.length, locale) : KHONG_CO}</span>,
    },
    {
      title: t('bienBan.cot.trangThai'),
      width: 180,
      render: (_v, p) => {
        const hanMuc = p.status === 'FAILED'
          ? tachLoiHanMuc(p.error_message, p.error_code)
          : p.summary_status === 'FAILED' ? tachLoiHanMuc(p.summary_error) : null;
        return (
          <div className="flex min-w-0 flex-col items-start gap-1">
            <div className="flex flex-wrap gap-1">
              <Badge variant={NHAN_TRANG_THAI[p.status].variant}>{t(NHAN_TRANG_THAI[p.status].khoa)}</Badge>
              {p.status === 'DONE' && p.summary_status === 'FAILED' && <Badge variant={BADGE_TOM_TAT.FAILED.variant}>{t('bienBan.tomTatLoiNgan')}</Badge>}
            </div>
            {hanMuc && <ThongBaoHetHanMuc bienThe="o" thongTin={hanMuc} />}
          </div>
        );
      },
    },
    {
      title: t('bienBan.cot.nguoiTao'),
      width: 170,
      render: (_v, p) => <span className="whitespace-nowrap text-xs text-content-secondary">{p.created_by_name ?? KHONG_CO}</span>,
    },
    {
      title: t('bienBan.cot.ngayTao'),
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => <span className="whitespace-nowrap text-xs tabular-nums text-content-muted">{ngayGioVN(v, locale)}</span>,
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, p) => (
        <MenuThaoTac
          tenBanGhi={p.title}
          muc={[
            { khoa: 'xem', nhan: t('bienBan.moPhien'), icon: <EyeOutlined />, nhom: 'xem', onChon: () => dieuHuong(`/bien-ban/${p.id}`) },
            { khoa: 'word', nhan: t('bienBan.xuatWord'), icon: <FileWordOutlined />, nhom: 'xem', hien: p.status === 'DONE', onChon: () => xuat(p, 'docx') },
            { khoa: 'txt', nhan: t('bienBan.xuatVanBan'), icon: <FileTextOutlined />, nhom: 'xem', hien: p.status === 'DONE', onChon: () => xuat(p, 'txt') },
            { khoa: 'doi-ten', nhan: t('bienBan.doiTen'), icon: <EditOutlined />, hien: quyen.sua, onChon: () => setDoiTen(p) },
            {
              khoa: 'thu-lai',
              nhan: t('bienBan.thuLaiPhien'),
              icon: <ReloadOutlined />,
              hien: quyen.sua && p.status === 'FAILED',
              onChon: () => ghi.thuLai.mutate(p.id, {
                onSuccess: () => toast.success(t('bienBan.daThuLai')),
                onError: (e) => toast.error(dichLoi(e)),
              }),
            },
            { khoa: 'xoa', nhan: t('bienBan.xoaPhien'), icon: <DeleteOutlined />, nhom: 'pha-huy', hien: quyen.xoa, onChon: () => setXoa(p) },
          ]}
        />
      ),
    },
  ];

  if (!quyen.doc) {
    return (
      <div className="flex h-full flex-col bg-subtle p-4">
        <Empty icon={AudioLines} title={t('bienBan.khongCoQuyen')} description={t('bienBan.khongCoQuyenMo')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          titleIcon={<AudioLines size={20} className="text-primary-600" aria-hidden />}
          title={tieuDe}
          actions={
            <>
              <Button variant="ghost" size="md" title={t('chung.lamMoi')} aria-label={t('chung.lamMoi')} onClick={() => void q.refetch()}>
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              {quyen.tao && (
                <Button variant="primary" size="md" icon={<CloudUploadOutlined />} onClick={moHopTai}>{t('bienBan.taiBanGhi')}</Button>
              )}
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('bienBan.timPlaceholder')}
            aria-label={t('bienBan.timNhan')}
            value={tuKhoa}
            onChange={(e) => goTim(e.target.value)}
          />
          <Select<LocTrangThai>
            className="w-full sm:w-56"
            aria-label={t('bienBan.locTrangThai')}
            value={trangThai}
            onChange={setTrangThai}
            options={[
              { value: 'tat-ca', label: t('bienBan.tatCaTrangThai') },
              ...TRANG_THAI_PHIEN.map((s) => ({ value: s, label: t(NHAN_TRANG_THAI[s].khoa) })),
            ]}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('bienBan.loiDanhSach', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>{t('chung.thuLai')}</Button>
            </div>
          ) : (
            <>
              <DataTable<Phien>
                caption={t('bienBan.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={q.isLoading}
                size="middle"
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: q.isLoading ? (
                    <div className="h-48" />
                  ) : (
                    <Empty
                      icon={AudioLines}
                      title={coLoc ? t('bienBan.rongLoc') : t('bienBan.rongThat')}
                      description={coLoc ? undefined : t('bienBan.rongThatMo')}
                      action={
                        coLoc || !quyen.tao ? undefined : (
                          <Button variant="primary" size="md" icon={<CloudUploadOutlined />} onClick={moHopTai}>{t('bienBan.taiBanGhiDauTien')}</Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {t(q.hasNextPage ? 'bienBan.dangHienCon' : 'bienBan.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                  {q.hasNextPage && (
                    <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{t('chung.taiThem')}</Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <HopTaiBanGhi
        key={lanMoTai}
        open={moTai}
        onClose={() => setMoTai(false)}
        onDaTao={(p) => {
          setMoTai(false);
          toast.success(t('bienBanTai.daGui'));
          dieuHuong(`/bien-ban/${p.id}`);
        }}
      />
      <HopDoiTenPhien key={doiTen?.id ?? 'dong'} phien={doiTen} onClose={() => setDoiTen(null)} />
      <HopXacNhanThaoTac
        open={Boolean(xoa)}
        tieuDe={t('bienBan.xacNhanXoa.tieuDe', { ten: xoa?.title ?? '' })}
        than={t('bienBan.xacNhanXoa.than')}
        nhanXacNhan={t('bienBan.xacNhanXoa.nut')}
        nguyHiem
        dangChay={ghi.xoa.isPending}
        loi={ghi.xoa.error ? dichLoi(ghi.xoa.error) : null}
        onClose={() => {
          setXoa(null);
          ghi.xoa.reset();
        }}
        onXacNhan={xacNhanXoa}
      />
    </div>
  );
}
